/**
 * Where a watched video stops by itself: the end of each section of the map it teaches, written by the
 * agent to `video-stops/<name>.json` (ADR-0041). Plain .ts so `node --test` can load it.
 */
import { formatWatching } from "../../../shared/watching/line.ts";

export interface VideoStop {
	/** Seconds into the video, a time the material wrote. */
	at: number;
	section: string;
	name: string;
}

export interface StopPlan {
	/** Off once the user asked not to be stopped. */
	enabled: boolean;
	stops: VideoStop[];
}

/** What the panel sends at a stop, after the watching line; the `watch` skill listens for it. */
export const STOP_TEXT = "（这一节到这里）";

/** Playback moves a quarter of a second between time updates; a longer jump is someone seeking. */
const MAX_STEP = 2;

/** A file that cannot be read stops nowhere; a stop without a time is dropped. */
export function parseStops(text: string): StopPlan {
	try {
		const raw = JSON.parse(text) as { enabled?: unknown; stops?: unknown };
		const stops = (Array.isArray(raw.stops) ? raw.stops : [])
			.filter((s): s is VideoStop => typeof s?.at === "number" && s.at >= 0)
			.map((s) => ({ at: s.at, section: String(s.section ?? ""), name: String(s.name ?? "") }))
			.sort((a, b) => a.at - b.at);
		return { enabled: raw.enabled !== false, stops };
	} catch {
		return { enabled: false, stops: [] };
	}
}

/** The stop the video just played across, going forward, that has not been made yet. */
export function crossedStop(plan: StopPlan, from: number, to: number, fired: readonly number[]): VideoStop | null {
	if (!plan.enabled || to <= from || to - from > MAX_STEP) return null;
	return plan.stops.find((s) => s.at > from && s.at <= to && !fired.includes(s.at)) ?? null;
}

export function stopMessage(file: string, at: number): string {
	return `${formatWatching(file, at)}${STOP_TEXT}`;
}

/** Beside the video's notes, named after the uploaded audio as they are. */
export function stopsPathFor(audioPath: string): string {
	const base = audioPath.slice(audioPath.lastIndexOf("/") + 1);
	const dot = base.lastIndexOf(".");
	return `video-stops/${dot > 0 ? base.slice(0, dot) : base}.json`;
}
